import {
  EmailHistoryItem,
  useEmailHistory
} from '@services/sendEmail/queries/useEmailHistory'
import DOMPurify from 'dompurify'
import { History, UserRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'

function formatWhen(iso: string) {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function HistoryItem({ item }: { item: EmailHistoryItem }) {
  const { t } = useTranslation('admin')
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-2xl border border-ink/[0.06] bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-ink">
          <UserRound className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="truncate">
            {item.sender_name || t('sendEmail.history.unknownSender')}
          </span>
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatWhen(item.created_at)}
        </span>
      </div>

      <div className="mt-2 font-semibold text-ink">{item.subject}</div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {item.groups.map((g) => (
          <span
            key={g}
            className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary"
          >
            {t(`sendEmail.groups.${g}`)}
          </span>
        ))}
        {item.recipient_emails.map((email) => (
          <span
            key={email}
            className="rounded-full bg-ink/[0.06] px-2.5 py-0.5 text-xs font-medium text-ink/70"
          >
            {email}
          </span>
        ))}
        <span className="rounded-full bg-peach/25 px-2.5 py-0.5 text-xs font-semibold text-warning">
          {t('sendEmail.history.recipientsCount', {
            count: item.recipients_count
          })}
        </span>
      </div>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-3 border-0 bg-transparent p-0 text-sm font-medium text-primary hover:underline"
      >
        {open
          ? t('sendEmail.history.hideMessage')
          : t('sendEmail.history.showMessage')}
      </button>

      {open && (
        <div
          className="tiptap mt-2 rounded-xl bg-ink/[0.02] p-3 text-sm text-ink/85"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(item.message_text, {
              USE_PROFILES: { html: true }
            })
          }}
        />
      )}
    </div>
  )
}

export function EmailHistoryPanel() {
  const { t } = useTranslation('admin')
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useEmailHistory()
  const items = data?.pages.flatMap((p) => p.results) ?? []
  const count = data?.pages[0]?.count

  return (
    <div className="relative min-h-[24rem]">
      <Card className="absolute inset-0 flex flex-col overflow-hidden p-0">
      <div className="flex items-center gap-2.5 border-b border-ink/[0.06] px-5 py-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <History className="h-4 w-4" />
        </span>
        <h2 className="m-0 flex-1 font-display text-base font-bold tracking-tight text-ink">
          {t('sendEmail.history.title')}
        </h2>
        {count != null && (
          <span className="rounded-full bg-ink/[0.06] px-2.5 py-0.5 text-xs font-semibold text-ink/60">
            {count}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-2xl bg-ink/[0.04]"
            />
          ))
        ) : items.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-muted-foreground">
            {t('sendEmail.history.empty')}
          </div>
        ) : (
          <>
            {items.map((item) => (
              <HistoryItem key={item.id} item={item} />
            ))}
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full rounded-2xl border border-ink/[0.12] bg-transparent py-2.5 text-sm font-medium text-ink/70 transition-colors hover:bg-ink/[0.04] hover:text-ink disabled:opacity-50"
              >
                {isFetchingNextPage ? '…' : t('sendEmail.history.loadMore')}
              </button>
            )}
          </>
        )}
      </div>
      </Card>
    </div>
  )
}
